if (!customElements.get("login-form")) {
  class LoginForm extends HTMLElement {
    constructor() {
      super();
      this.apiBaseUrl = `${window.theme.auth.apiDomain}/api`;
      this.constants = {
        DEFAULT_ERROR_MESSAGE: "Something went wrong. Try again or contact us if you continue to see this message.",
        LINK_EXPIRED_MESSAGE: "Password reset link expired.",
        EMAIL_EXISTS_MESSAGE: "This email already exists. Please login instead.",
        ALREADY_VERIFIED_MESSAGE: "Account already verified.",
        NOT_VERIFIED_MESSAGE: "Your email address has not been verified",
        VERIFICATION_LINK_EXPIRED_MESSAGE: "Verification link expired.",
        MIGRATE_ACCOUNT_MESSAGE: "MIGRATE_ACCOUNT"
      };
      this.checkoutUrl = this.getParam("checkout_url");

      this.loginFormContainer = this.querySelector("#login");
      this.loginForm = this.loginFormContainer.querySelector(".js-login-form");
      this.loginBtn = this.querySelector(".js-login-button");
      this.loginBtn.addEventListener("click", this.handleLogin.bind(this));
      this.querySelector(".js-login").addEventListener("click", (e) => this.handleFormLinkClick(e, "login"));

      this.recoverFormContainer = this.querySelector("#recover");
      this.recoverForm = this.recoverFormContainer.querySelector(".js-recover-form");
      this.recoverBtn = this.querySelector(".js-recover-button");
      this.recoverBtn.addEventListener("click", this.handleRecover.bind(this));
      this.querySelector(".js-recover").addEventListener("click", (e) => this.handleFormLinkClick(e, "recover"));

      this.verifyFormContainer = this.querySelector("#verify");
      this.verifyForm = this.verifyFormContainer.querySelector(".js-verify-form");
      this.verifyBtn = this.querySelector(".js-verify-button");
      this.verifyBtn.addEventListener("click", this.handleVerify.bind(this));

      this.resetFormContainer = this.querySelector("#reset");
      this.resetForm = this.resetFormContainer.querySelector(".js-reset-form");
      this.resetBtn = this.querySelector(".js-reset-button");
      this.resetBtn.addEventListener("click", this.handleReset.bind(this));

      this.formIds = ["recover", "verify", "login", "reset"];
      this.handleFormContent();
    }

    handleFormContent() {
      const idFromHash = window.location.hash ? window.location.hash.slice(1)?.split("?")[0] : null;

      if (this.formIds.includes(idFromHash)) {
        this.showFormContent(idFromHash);
      } else {
        this.showFormContent("login");
      }
    }

    /**
     * @param {*} e 
     * @param {*} type: "login" | "recover" | "verify" | "reset"
     */
    handleFormLinkClick(e, type = "login") {
      e.preventDefault();
      this.showFormContent(type);
      history.pushState("", document.title, `${window.location.pathname}${type !== 'login' ? `#${type}` : ''}`);
    }

    /**
     * @param {object} e - Event object.
     */
    async handleLogin(e) {
      e.preventDefault();

      // Disable button until submission is complete.
      if (this.loginBtn.getAttribute("aria-disabled") === "true") return;

      this.onFormLoading("login");
      const formData = Object.fromEntries(new FormData(this.loginForm));

      if (!formData || !formData["customer[email]"] || !formData["customer[password]"]) {
        this.setStatusMessage("Please fill in both fields.", "login", "error");
        this.toggleLoading("login", false);
        return;
      }

      try {
        const { error, success } = await this.signIn(formData["customer[email]"], formData["customer[password]"]);

        if (error?.fields) {
          this.handleFieldErrors(error.fields, "login");
        }

        if (error?.message) {
          if (formData && (error.message === this.constants.MIGRATE_ACCOUNT_MESSAGE || error.message === this.constants.NOT_VERIFIED_MESSAGE)) {
            return this.handleMigrateAccount(formData["customer[email]"], formData["customer[password]"], error.message === this.constants.NOT_VERIFIED_MESSAGE);
          } else {
            this.setStatusMessage(error.message, "login", "error");
          }
        }

        if (success) {
          this.setStatusMessage("Login successful, one moment...", "login", "success", true);

          setTimeout(() => {
            window.location.href = success;
          }, 0)
        } else {
          this.toggleLoading("login", false);
        }
      } catch (e) {
        console.log('error', e);

        this.setStatusMessage("Something went wrong. Please contact us if you continue to have problems.", "login", "error");
        this.toggleLoading("login", false);
      }
    }

    /**
     * @param {object} e - Event object.
     */
    async handleRecover(e) {
      e.preventDefault();

      // Disable button until submission is complete.
      if (this.recoverBtn.getAttribute("aria-disabled") === "true") return;

      this.onFormLoading("recover");
      const formData = Object.fromEntries(new FormData(this.recoverForm));

      if (!formData || !formData["email"]) {
        this.setStatusMessage("Please fill in the email field.", "recover", "error");
        this.toggleLoading("recover", false);
        return;
      }

      const { error, success } = await this.recover(formData["email"]);

      if (error?.fields) {
        this.handleFieldErrors(error.fields, "recover");
      }

      if (error?.message) {
        this.setStatusMessage(error.message, "recover");
      }

      if (success) {
        this.setStatusMessage(["Check your email!", "You'll receive an email shortly with instructions for resetting your password."], "recover", "info", true);
        this.recoverForm.reset();
      }

      this.toggleLoading("recover", false);
    }

    /**
     * @param {object} e - Event object.
     */
    async handleVerify(e) {
      e.preventDefault();

      // Disable button until submission is complete.
      if (this.verifyBtn.getAttribute("aria-disabled") === "true") return;

      this.onFormLoading("verify");

      if (this.verifyBtn.dataset.resend === "true") {
        return this.handleVerificationLinkExpired();
      }

      const formData = Object.fromEntries(new FormData(this.verifyForm));

      if (!formData || !formData["firstName"]) {
        this.setStatusMessage("Please type your first name.", "verify", "error");
        this.toggleLoading("verify", false);
        return;
      }

      const { error, success } = await this.verifyEmail();
      
      if (error?.message) {
        if (error.message === this.constants.VERIFICATION_LINK_EXPIRED_MESSAGE) {
          this.setStatusMessage(["Verification link expired.", `Click below to resend verification email.`], "verify", "error");
          this.verifyForm.querySelector(".form__field").classList.add("js-hidden");
          this.verifyBtn.setAttribute("data-resend", "true");
          this.verifyBtn.innerHTML = "Resend verification email";
        } else {
          this.setStatusMessage(this.constants.DEFAULT_ERROR_MESSAGE, "verify", "error");
        }
      }

      if (success) {
        this.setStatusMessage("Email successfully verified. One moment...", "verify", "success", true);

        await this.navigateToAccount(formData["firstName"]);
      } else {
        this.toggleLoading("verify", false);
      }
    }

    /**
     * @param {object} e - Event object.
     */
    async handleReset(e) {
      e.preventDefault();

      // Disable button until submission is complete.
      if (this.resetBtn.getAttribute("aria-disabled") === "true") return;

      this.onFormLoading("reset");
      const formData = Object.fromEntries(new FormData(this.resetForm));

      if (!formData || !formData["password"] || !formData["confirm-password"]) {
        this.setStatusMessage("Please fill in the both fields.", "reset", "error");
        this.toggleLoading("reset", false);
        return;
      }

      if (formData["password"] !== formData["confirm-password"]) {
        this.setStatusMessage("Passwords don't match.", "reset", "error");
        this.toggleLoading("reset", false);
        return;
      }

      const { error, success } = await this.reset(formData["password"], this.resetForm.dataset.token);

      if (error?.fields) {
        this.handleFieldErrors(error.fields, "reset");
      }

      if (error?.message) {
        this.setStatusMessage(error.message, "reset");

        if (error.message === this.constants.LINK_EXPIRED_MESSAGE) {
          location.href = "/account/login#recover";
          return;
        }
      }

      if (success) {
        this.setStatusMessage(["Password updated!", `Login to your account <a class="text-success-text link" href="/account/login" />here</a>.`], "reset", "success", true);
        this.resetForm.reset();
      }

      this.toggleLoading("reset", false);
    }

    async navigateToAccount(firstName) {
      const returnTo = this.getParam("return_to");
      const data = returnTo ? { firstName, returnTo } : { firstName };
      const url = await this.generateMultipassUrl(data);

      window.location.href = url ? url : "/account/login";
    }

    async handleVerificationLinkExpired() {
      const { error, success } = await this.sendVerificationEmail();
        
      if (error?.fields) {
        this.handleFieldErrors(error.fields, "verify");

        if (error.fields.email === this.constants.EMAIL_EXISTS_MESSAGE) {
          this.setStatusMessage(["This email already exists!", "Please login instead."],  "verify", "info");
        }
      }

      if (error?.message) {
        this.setStatusMessage(error.message, "verify", "error");
      }

      if (success) {
        this.setStatusMessage(["Check your email!", "You should receive an email shortly with a link to complete account verification."], "verify", "info", true);
        this.verifyBtn.classList.add("js-hidden");
      }

      this.toggleLoading("verify", false);
    }

    async verifyEmail() {
      try {
        const response = await supertokensEmailVerification.verifyEmail();

        if (response.status === "EMAIL_VERIFICATION_INVALID_TOKEN_ERROR") {
          return {
            error: this.formatErrorRes({ message: this.constants.VERIFICATION_LINK_EXPIRED_MESSAGE }),
            success: false,
          }
        }

        return {
          error: null,
          success: true,
        }
      } catch (e) {
        return this.catchError(e)
      }
    }

    async reset(password, token) {
      try {
        const response = await supertokensEmailPassword.submitNewPassword({
          formFields: [
            {
              id: 'password',
              value: password,
            },
          ],
          token,
        });

        if (response.status === 'FIELD_ERROR') {
          return {
            error: this.formatErrorRes({ fields: this.formatFieldErrors(response.formFields) }),
            success: false,
          }
        }

        if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
          return {
            error: this.formatErrorRes({
              message: [this.constants.LINK_EXPIRED_MESSAGE, `Please try resetting it again <a class="link text-error-text" href="/account/login#recover">here</a>.`],
            }),
            success: false,
          }
        }

        if (response.status !== 'OK') throw this.constants.DEFAULT_ERROR_MESSAGE;

        return {
          error: null,
          success: true,
        }
      } catch (e) {
        console.error(e)
        return this.catchError(e);
      }
    }

    async recover(email) {
      try {
        const { error } = await this.checkEmailExists(email);

        if (!error || error.message !== 'Email already exists. Please try logging in.') {
          return {
            error: this.formatErrorRes({
              message: error?.message || ["Email does not exist.", `Please <a class="link text-error-text" href="${theme.routes.register}">register an account</a>.`],
            }),
            success: false,
          }
        }

        const response = await supertokensEmailPassword.sendPasswordResetEmail({
          formFields: [
            {
              id: 'email',
              value: email,
            },
          ],
        });

        if (response.status === 'FIELD_ERROR') {
          return {
            error: this.formatErrorRes({ fields: this.formatFieldErrors(response.formFields) }),
            success: false,
          }
        }

        if (response.status !== 'OK') throw this.constants.DEFAULT_ERROR_MESSAGE

        return {
          error: null,
          success: true,
        }
      } catch (e) {
        return this.catchError(e);
      }
    }

    /*
      type: "migrate" | "resend"
    */
    async handleMigrateAccount(email, password, resendEmail = false) {
      const { error, success } = resendEmail ? await this.sendVerificationEmail() : await this.migrate(email, password);
      const migrateMessage = ["Check your email!", `We need to verify your account. Please check your email to confirm your login details${this.checkoutUrl ? " and checkout" : ""}.`]

      if (error?.fields) {
        this.handleFieldErrors(error.fields, "login");

        if (error.fields.email === this.constants.EMAIL_EXISTS_MESSAGE) {
          this.setStatusMessage(["This email already exists!", "Please login instead."],  "login", "info");
        }
      }

      if (error?.message) {
        this.setStatusMessage(error.message, "login", "error");
      }

      if (success) {
        this.setStatusMessage(migrateMessage, "login", "info", true);

        this.loginForm.reset();
      }

      this.toggleLoading("login", false);
    }

    getParam(param) {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get(param);
    }

    async checkEmailExists(email) {
      try {
        const response = await supertokensEmailPassword.doesEmailExist({
          email,
        })

        if (response.doesExist) {
          return {
            error: this.formatErrorRes({ message: 'Email already exists. Please try logging in.' }),
            success: false,
          }
        }

        if (response.status !== 'OK') throw this.constants.DEFAULT_ERROR_MESSAGE

        return {
          error: null,
          success: true,
        }
      } catch (e) {
        return this.catchError(e)
      }
    }

    async sendVerificationEmail() {
      try {
        const response = await supertokensEmailVerification.sendVerificationEmail();

        if (response.status === 'EMAIL_ALREADY_VERIFIED_ERROR') {
          return {
            error: this.formatErrorRes({
              message: this.constants.ALREADY_VERIFIED_MESSAGE,
            }),
            success: false,
          }
        }

        return {
          error: null,
          success: true,
        }
      } catch (e) {
        console.error(e)
        return this.catchError(e);
      }
    }

    async migrate(email, password) {
      try {
        const response = await supertokensEmailPassword.signUp({
          formFields: [
            {
              id: "email",
              value: email,
            },
            {
              id: "password",
              value: password,
            },
          ],
        })

        if (response.status === "FIELD_ERROR") {
          return {
            error: this.formatErrorRes({ fields: this.formatFieldErrors(response.formFields) }),
            success: false,
          }
        }

        if (response.status === "WRONG_CREDENTIALS_ERROR") {
          return {
            error: this.formatErrorRes({
              message: "Incorrect email and password combination. Please check your details and try again.",
            }),
            success: false,
          }
        }

        if (response.status !== "OK") {
          throw this.constants.DEFAULT_ERROR_MESSAGE
        }

        return this.sendVerificationEmail();
      } catch (e) {
        console.error(e)
        return this.catchError(e)
      }
    }

    async signIn(email, password) {
      try {
        const response = await supertokensEmailPassword.signIn({
          formFields: [
            {
              id: "email",
              value: email,
            },
            {
              id: "password",
              value: password,
            },
          ],
        })
    
        if (response.status === "FIELD_ERROR") {
          return {
            error: this.formatErrorRes({ fields: this.formatFieldErrors(response.formFields) }),
            success: false,
          }
        }
    
        if (response.status === "WRONG_CREDENTIALS_ERROR") {
          // Check email exists in supertokens
          const existsInSTRes = await supertokensEmailPassword.doesEmailExist({
            email,
          })
          const existsInSuperTokens = !!existsInSTRes?.doesExist
    
          if (!existsInSuperTokens) {
            // Check email exists in Shopify
            const existsInShopify = await this.checkEmailExistsInShopify(email)
    
            if (existsInShopify) {
              // Show migrate form
              return {
                error: this.formatErrorRes({
                  message: this.constants.MIGRATE_ACCOUNT_MESSAGE,
                }),
                success: false,
              }
            }
          }
    
          // Else show error for wrong credentials
          return {
            error: this.formatErrorRes({
              message: "Incorrect email and password combination. Please check your details and try again.",
            }),
            success: false,
          }
        }
    
        if (response.status !== "OK") {
          throw this.constants.DEFAULT_ERROR_MESSAGE
        }
    
        const url = await this.generateMultipassUrl({ returnTo: "/account" })
    
        return {
          error: null,
          success: url,
        }
      } catch (e) {
        if (e?.status === 403) {
          return {
            error: this.formatErrorRes({ message: this.constants.NOT_VERIFIED_MESSAGE }),
            success: false,
          }
        }
    
        console.error(e)
        return this.catchError(e)
      }
    }

    async checkEmailExistsInShopify(email) {
      const res = await fetch(`${this.apiBaseUrl}/customers/exists`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })
    
      if (!res.ok) throw "Something went wrong"
    
      const json = await res.json()
    
      return !!json?.exists
    }

    async generateMultipassUrl({ firstName, lastName, returnTo }) {
      const multipassRes = await fetch(`${this.apiBaseUrl}/multipass/url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          returnTo,
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
        }),
      })
    
      // Not verified
      if (multipassRes.status === 403) throw multipassRes
      if (!multipassRes.ok) {
        throw "Failed to login to Shopify. Try again or contact us if you continue to see this message."
      }
    
      const { url } = await multipassRes.json()
    
      return url || null
    }


    formatErrorRes(errors) {
      const defaults = {
        fields: null,
        message: null,
      }

      return {
        ...defaults,
        ...errors,
      }
    }

    formatFieldErrors(formFields) {
      return formFields.reduce((acc, cur) => ({ ...acc, [cur.id]: cur.error }), {})
    }

    async getErrorMessage(e) {
      if (e.isSuperTokensGeneralError === true) return e.message
      if (typeof e === "string") return e

      return this.constants.DEFAULT_ERROR_MESSAGE
    }

    async catchError(e) {
      return {
        error: this.formatErrorRes({
          message: await this.getErrorMessage(e),
        }),
        success: false,
      }
    }

    /*
      type: "verify" | "login" | "reset" | "recover"
    */
    onFormLoading(formType) {
      this.statusEl = null;
      this.setStatusMessage(null, formType);
      this.toggleLoading(formType);
      this.clearFieldErrors();
    }

    toggleLoading(view = "login", isLoading = true) {
      const button = this.getButtonFromView(view);

      if (isLoading) {
        button.setAttribute("aria-disabled", "true");
        button.classList.add("is-loading");
      } else {
        button.classList.remove("is-loading");
        button.removeAttribute("aria-disabled");
      }
    }

    getButtonFromView(view) {
      switch (view) {
        case "login":
          return this.loginBtn;
        case "recover":
          return this.recoverBtn;
        case "verify":
          return this.verifyBtn;
        case "reset":
          return this.resetBtn;
      }
    }

    /*
      message: string
      form: "login" | "recover"
      type: "error" | "success" | "info"
    */
    setStatusMessage(message, form, type = "error", hideFields = false) {
      this.statusEl = this.statusEl || this.querySelector(`.js-${form}-form-status`);
      if (!this.statusEl) return;

      this.statusEl.hidden = !message;

      if (message) {
        this.statusEl.setAttribute("data-status", type);
        const messageArray = Array.isArray(message) ? message : [message];
        this.statusEl.innerHTML = messageArray.map((line, index) => {
          return index === 0 ? `<p><b>${line}</b></p>` : `<p>${line}</p>`;
        }).join("");

        if (hideFields) { 
          this.statusEl.closest("form").dataset.hideFields = true;
        }
      }
    }

    /*
      errors: string[]
      form: "login" | "recover"
    */
    handleFieldErrors(errors, form) {
      console.log(errors, form)
      switch (form) {
        case "login":
          if (errors?.email) {
            this.setFieldError(this.loginForm.querySelector('input[name="customer[email]"]'), errors.email);
          }
          if (errors.password) {
            this.setFieldError(this.loginForm.querySelector('input[name="customer[password]"]'), errors.password);
          }
          break;
        case "recover":
          if (errors.email) {
            this.setFieldError(this.recoverForm.querySelector('input[name="email"]'), errors.email);
          }
        case "reset":
          if (errors.password) {
            this.setFieldError(this.resetForm.querySelector('input[name="password"]'), errors.password);
          }
          break;
      }
    }

    setFieldError(input, error) {
      if (!input) return;

      const id = `${input.id}-error`;
      input.setAttribute("aria-invalid", "true");
      input.setAttribute("aria-describedby", id);
      input.insertAdjacentHTML("afterend", this.getFieldErrorHTML(error, id));
    }

    getFieldErrorHTML(error, id) {
      return `<p class="form__field-error" id="${id}">${error}</p>`
    }

    clearFieldErrors() {
      this.querySelectorAll('input').forEach(input => {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      });

      this.querySelectorAll('.form__field-error').forEach(el => el.remove());
    }

    /**
     * 
     * @param type: "recover" | "verify" | "login" | "reset"
     */

    showFormContent(type = "login") {
      const containers = [this.loginFormContainer, this.recoverFormContainer, this.verifyFormContainer, this.resetFormContainer];

      containers.forEach(c => {
        if (c.id !== type) {
          c.classList.add('js-hidden');
        } else {
          c.classList.remove('js-hidden');
        }
      });
    }
  }

  customElements.define("login-form", LoginForm);
}
